import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import loadable from '@loadable/component';
import { Switch, Route, useHistory } from 'react-router-dom';

import CtaButton from 'components/essentials/Cta/CtaButton';
import IconVerified from 'components/icons/iconVerified';
import Placeholder from 'components/consumer/Placeholder';

import BrowserOnly from 'components/consumer/BrowserOnly';
import getShortCustomerName from 'commons/getShortCustomerName';
import useScreen from 'hooks/useScreen';
import ReviewStars from 'components/consumer/ReviewStars';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import RatingAndReviews from '../../RatingAndReviews';
import PDPCustomerPhotosGrid from './PDPCustomerPhotosGrid';

const PDPCustomerPhotosReviewsModal = loadable(() =>
  import('./PDPCustomerPhotosReviewsModal')
);

const Comment = ({ comment }) => {
  const maxCount = 175;
  if (comment && comment.length > maxCount) {
    return (
      <>
        <SanitizedHtml as="span" html={comment.slice(0, maxCount)} />
        {`... `}
        <CtaButton
          styling="link"
          color="brand"
          size="sm"
          spacing="sm"
          hasArrow={false}
          className="normal-case hover:underline"
        >
          Continue reading
        </CtaButton>
      </>
    );
  }
  return comment;
};

Comment.propTypes = {
  comment: PropTypes.string,
};

const PDPCustomerPhotosUI = ({
  id,
  name,
  loading,
  customerPhotos,
  customerReviews,
  pdpIsModularBYOProduct,
  selectedMaterial,
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(-1);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(-1);
  const [totalCount, setTotalCount] = useState(0);
  const history = useHistory();

  useEffect(() => {
    setTotalCount(
      (customerPhotos?.totalPhotos || 0) + (customerReviews?.totalReviews || 0)
    );
  }, [pdpIsModularBYOProduct, customerPhotos, customerReviews]);

  const toggleModal = () => {
    setSelectedPhotoIndex(-1);
    setSelectedReviewIndex(-1);
    const currentPath = window.location.pathname;
    if (currentPath.split('/').pop() === 'customer-reviews') {
      history.replace(
        `${currentPath.substring(0, currentPath.lastIndexOf('/'))}/`
      );
      return;
    }
    history.goBack();
  };

  const openModal = () => {
    history.push('customer-reviews');
  };

  const { isMobile } = useScreen();

  const filteredReview = useMemo(() => {
    if (customerReviews?.reviews.length) {
      const reviewForSelectedFabrics = customerReviews?.reviews.filter(review =>
        review?.materials.includes(selectedMaterial.label)
      );

      const reviewForOtherFabrics = customerReviews?.reviews.filter(
        review => !review?.materials.includes(selectedMaterial.label)
      );

      return [...reviewForSelectedFabrics, ...reviewForOtherFabrics];
    }
    return [];
  }, [customerReviews?.reviews, selectedMaterial]);

  const filteredPhotos = useMemo(() => {
    if (customerPhotos?.photos.length) {
      const reviewForSelectedFabrics = customerPhotos?.photos
        .filter(
          review =>
            parseInt(review?.upholstery_id, 10) === selectedMaterial.materialId
        )
        .map(image => ({ ...image, url: image.url.split('main_').join('') }));

      const reviewForOtherFabrics = customerPhotos?.photos
        .filter(
          review =>
            parseInt(review?.upholstery_id, 10) !== selectedMaterial.materialId
        )
        .map(image => ({ ...image, url: image.url.split('main_').join('') }));

      return [...reviewForSelectedFabrics, ...reviewForOtherFabrics];
    }
    return [];
  }, [customerPhotos?.photos, selectedMaterial]);

  if (loading) {
    return (
      <div
        className="[border-top:1px_solid_#e5e5e5] mt-14 pt-14 [content-visibility:auto] [contain-intrinsic-size:0]"
        id="PDPCustomerPhotos"
      >
        <div className="text-xl font-bold mb-1 [&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c]">
          <Placeholder type="rect" loading style={{ width: 331, height: 28 }} />
        </div>

        <div>
          <Placeholder type="rect" loading style={{ width: 174, height: 26 }} />
        </div>

        <div className="grid gap-[.5rem] mt-4 mb-2 grid-cols-[repeat(4,calc(25%_-_.5rem)_)] grid-rows-[repeat(2,100px)] justify-center content-end [content-visibility:auto] [contain-intrinsic-size:0] PDPCustomerPhotosGrid__layout-5">
          <div className="rounded overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:cursor-pointer [&_img]:object-cover [&_img]:[content-visibility:auto] [&_img]:[contain-intrinsic-size:0] PDPCustomerPhotosGrid__item1">
            <div className="w-full h-full">
              <Placeholder type="rect" loading />
            </div>
          </div>

          <div className="rounded overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:cursor-pointer [&_img]:object-cover [&_img]:[content-visibility:auto] [&_img]:[contain-intrinsic-size:0] PDPCustomerPhotosGrid__item2">
            <div className="w-full h-full">
              <Placeholder type="rect" loading />
            </div>
          </div>

          <div className="rounded overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:cursor-pointer [&_img]:object-cover [&_img]:[content-visibility:auto] [&_img]:[contain-intrinsic-size:0] PDPCustomerPhotosGrid__item3">
            <div className="w-full h-full">
              <Placeholder type="rect" loading />
            </div>
          </div>

          <div className="rounded overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:cursor-pointer [&_img]:object-cover [&_img]:[content-visibility:auto] [&_img]:[contain-intrinsic-size:0] PDPCustomerPhotosGrid__item4">
            <div className="w-full h-full">
              <Placeholder type="rect" loading />
            </div>
          </div>

          <div className="rounded overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:cursor-pointer [&_img]:object-cover [&_img]:[content-visibility:auto] [&_img]:[contain-intrinsic-size:0] PDPCustomerPhotosGrid__item5">
            <div className="w-full h-full">
              <Placeholder type="rect" loading />
            </div>
          </div>
        </div>

        <div className="mt-6 mx-0 mb-2">
          {[1, 2].map(item => (
            <React.Fragment key={`PDPCustomerPhotos__list${item}`}>
              <div className="relative flex cursor-pointer mx-0 my-8 max-[767px]:flex-col [&_img]:rounded-lg mb-8 pt-8 px-0 pb-0 [border-top:1px_solid_#f6f6f6] first:[border-top:0] first:mt-0">
                <div className="flex-[0.33] max-[767px]:flex max-[767px]:flex-1">
                  <div className="w-11 h-11 max-h-[3.75rem] text-2xl text-gray-light6 bg-gray-light8 rounded-full mr-4 mb-2 flex justify-center items-center">
                    <Placeholder
                      type="round"
                      loading
                      style={{ width: 44, height: 44 }}
                    />
                  </div>

                  <div className="PDPCustomerPhotos__user-details">
                    <div className="text-xl font-bold mb-1 [&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c]">
                      <Placeholder
                        type="rect"
                        loading
                        style={{
                          height: 14,
                          borderRight: !isMobile ? '57px solid white' : 0,
                        }}
                      />
                    </div>

                    <div className="text-xs">
                      <Placeholder
                        type="rect"
                        loading
                        style={{
                          height: 14,
                          borderRight: !isMobile ? '57px solid white' : 0,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="md:flex-[0.75]">
                  <div className="PDPCustomerPhotos__header">
                    <div className="[&_svg]:w-3 [&_svg]:mr-[.325rem]">
                      <Placeholder
                        type="rect"
                        loading
                        style={{ height: 14, width: 75, marginBottom: 13 }}
                      />
                    </div>
                  </div>

                  <div className="text-lg font-bold mt-[.325rem]">
                    <Placeholder
                      type="rect"
                      loading
                      style={{ height: 26, width: 252, marginBottom: 14 }}
                    />
                  </div>

                  <div className="text-sm mt-[.325rem]">
                    <Placeholder
                      type="rect"
                      loading
                      style={{ height: 14, marginBottom: 7 }}
                    />
                    <Placeholder
                      type="rect"
                      loading
                      style={{ height: 14, marginBottom: 7 }}
                    />
                    <Placeholder
                      type="rect"
                      loading
                      style={{ height: 14, marginBottom: 7 }}
                    />
                    <Placeholder type="rect" loading style={{ height: 14 }} />
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        <Placeholder
          type="rect"
          loading
          style={{ height: 44, marginTop: 46 }}
        />
      </div>
    );
  }

  if (!totalCount) {
    return <div className="PDPCustomerPhotosUI__filler" />;
  }

  const photos = filteredPhotos || [];
  const reviews = filteredReview || [];

  return (
    <>
      <div
        className="[border-top:1px_solid_#e5e5e5] mt-14 pt-14 [content-visibility:auto] [contain-intrinsic-size:0]"
        id="PDPCustomerPhotos"
      >
        {name && (
          <h2 className="text-xl font-bold mb-1 [&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c]">
            Customers love the {name}
          </h2>
        )}
        {customerReviews && (
          <div className="mb-6">
            <RatingAndReviews
              numberOfReviews={totalCount}
              rating={customerReviews.averageRating}
              size="sm"
            />
          </div>
        )}

        <PDPCustomerPhotosGrid
          photos={photos}
          totalPhotos={customerPhotos?.totalPhotos || 0}
          onPhotoClick={idx => {
            setSelectedPhotoIndex(idx);
            openModal();
          }}
        />

        <div className="mt-6 mx-0 mb-2">
          {reviews.slice(0, 2).map((review, idx) => (
            <React.Fragment key={`PDPCustomerPhotos__listItem${review.id}`}>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                className="relative flex cursor-pointer mx-0 my-8 max-[767px]:flex-col [&_img]:rounded-lg"
                onClick={() => {
                  setSelectedReviewIndex(idx);
                  openModal();
                }}
              >
                <div className="flex-[0.33] max-[767px]:flex max-[767px]:flex-1">
                  <div className="w-11 h-11 max-h-[3.75rem] text-2xl text-gray-light6 bg-gray-light8 rounded-full mr-4 mb-2 flex justify-center items-center">
                    {review.full_name?.[0]}
                  </div>
                  <div className="PDPCustomerPhotos__user-details">
                    <div className="text-xl font-bold mb-1 [&_svg]:ml-2 [&_svg_g]:stroke-[#107c8c] flex items-center justify-start">
                      <span className="text-sm font-bold">
                        {getShortCustomerName(review.full_name)}
                      </span>
                      <IconVerified />
                    </div>
                    <div className="text-xs">{review.location}</div>
                  </div>
                </div>
                <div className="md:flex-[0.75]">
                  <div className="PDPCustomerPhotos__header">
                    <ReviewStars
                      rating={parseInt(review.rate, 10)}
                      readOnly
                      size="xs"
                    />
                    <div className="text-xs absolute top-0 right-0">
                      {format(new Date(review.date), 'MMM dd, yyyy')}
                    </div>
                  </div>

                  <SanitizedHtml
                    className="text-lg font-bold mt-[.325rem]"
                    html={review.title}
                  />

                  <div className="text-sm mt-[.325rem]">
                    <Comment comment={review.comment} />
                  </div>
                </div>
              </div>
              {idx === 0 && <div className="[border-top:1px_solid_#f6f6f6]" />}
            </React.Fragment>
          ))}
        </div>
        {totalCount > 0 && (
          <CtaButton
            styling="solid-button"
            color="brand"
            onClick={openModal}
            className="w-full rounded-sm px-6 py-2"
            textClassName="!normal-case"
          >
            See all {totalCount} reviews and photos
          </CtaButton>
        )}
      </div>
      <Switch>
        <Route path="/modular/:slug/customer-reviews" exact>
          <BrowserOnly>
            <PDPCustomerPhotosReviewsModal
              productId={id}
              productName={name}
              toggleModal={toggleModal}
              totalReviews={totalCount}
              imgIndex={selectedPhotoIndex}
              reviewIndex={selectedReviewIndex}
              averageRating={customerReviews.averageRating}
              selectedMaterial={selectedMaterial}
            />
          </BrowserOnly>
        </Route>
        <Route path="/:category/:slug/customer-reviews">
          <BrowserOnly>
            <PDPCustomerPhotosReviewsModal
              productId={id}
              productName={name}
              toggleModal={toggleModal}
              totalReviews={totalCount}
              imgIndex={selectedPhotoIndex}
              reviewIndex={selectedReviewIndex}
              averageRating={customerReviews.averageRating}
              selectedMaterial={selectedMaterial}
            />
          </BrowserOnly>
        </Route>
      </Switch>
    </>
  );
};

PDPCustomerPhotosUI.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  loading: PropTypes.bool,
  customerReviews: PropTypes.shape({
    reviews: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        comment: PropTypes.string,
        date: PropTypes.string,
        full_name: PropTypes.string,
        location: PropTypes.string,
        rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      })
    ),
    totalReviews: PropTypes.number,
    averageRating: PropTypes.number,
  }),
  customerPhotos: PropTypes.shape({
    totalPhotos: PropTypes.number,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
      })
    ),
  }),
  pdpIsModularBYOProduct: PropTypes.bool,
  selectedMaterial: PropTypes.object,
};

export default PDPCustomerPhotosUI;
